import { z } from 'zod'

export const ARTICLE_STATUSES = [
  'draft',
  'scheduled',
  'generating',
  'generated',
  'publishing',
  'published',
  'failed',
  // Birth state of a translation sibling while TranslateArticleJob runs.
  // Backend: Article::STATUSES — must stay in sync or list parse fails.
  'translating',
  // An older article replaced by an autonomous refresh; kept for audit under a
  // technical slug, no longer the public content at its original URL.
  'superseded',
] as const
export type ArticleStatus = (typeof ARTICLE_STATUSES)[number]
export const zArticleStatus = z.enum(ARTICLE_STATUSES)

// Daily autonomous SEO/GEO research pass lifecycle. `skipped` = GSC configured
// but data too stale, or the per-tenant circuit breaker tripped.
export const RESEARCH_RUN_STATUSES = [
  'pending',
  'running',
  'completed',
  'failed',
  'skipped',
] as const
export type ResearchRunStatus = (typeof RESEARCH_RUN_STATUSES)[number]
export const zResearchRunStatus = z.enum(RESEARCH_RUN_STATUSES)

export const GENERATION_STAGES = ['ground', 'outline', 'draft', 'complete', 'failed'] as const
export type GenerationStage = (typeof GENERATION_STAGES)[number]
export const zGenerationStage = z.enum(GENERATION_STAGES)

export const GENERATION_EVENT_STAGES = [
  'created',
  'outline',
  'draft',
  'generated',
  'published',
  'failed',
] as const
export type GenerationEventStage = (typeof GENERATION_EVENT_STAGES)[number]
export const zGenerationEventStage = z.enum(GENERATION_EVENT_STAGES)

export const TOPIC_KINDS = ['pillar', 'spoke'] as const
export type TopicKind = (typeof TOPIC_KINDS)[number]
export const zTopicKind = z.enum(TOPIC_KINDS)

export const TOPIC_TEMPLATES = [
  'how-to',
  'listicle',
  'comparison',
  'roundup',
  'data-research',
  'faq-knowledge',
  'pillar-page',
] as const
export type TopicTemplate = (typeof TOPIC_TEMPLATES)[number]
export const zTopicTemplate = z.enum(TOPIC_TEMPLATES)

export const TOPIC_INTENTS = ['informational', 'commercial_investigation', 'transactional'] as const
export type TopicIntent = (typeof TOPIC_INTENTS)[number]
export const zTopicIntent = z.enum(TOPIC_INTENTS)

export const DELIVERY_STATUSES = ['pending', 'delivered', 'failed'] as const
export type DeliveryStatus = (typeof DELIVERY_STATUSES)[number]
export const zDeliveryStatus = z.enum(DELIVERY_STATUSES)

export const WEBHOOK_EVENTS = [
  'article.published',
  'article.updated',
  'article.unpublished',
] as const
export type WebhookEvent = (typeof WEBHOOK_EVENTS)[number]
export const zWebhookEvent = z.enum(WEBHOOK_EVENTS)

export const ADMIN_ROLES = ['super_admin', 'tenant_admin'] as const
export type AdminRole = (typeof ADMIN_ROLES)[number]
export const zAdminRole = z.enum(ADMIN_ROLES)

// Known brand slugs. NOTE: tenant.slug comes from the API and is validated as
// z.string() — this list documents the current brands but is NOT enforced.
export const TENANT_SLUGS = ['alerte-immo', 'patronome', 'reseaumobile'] as const
export type TenantSlug = (typeof TENANT_SLUGS)[number]
